import { useState } from "react";
import { useTranslation } from "react-i18next";
import { motion, AnimatePresence } from "framer-motion";
import { ChevronDown } from "lucide-react";
import { isForecastingEnabled } from "@/lib/features";

const getFaqs = () => [
    { qKey: "landing.faq.q1", aKey: "landing.faq.a1" },
    { qKey: "landing.faq.q2", aKey: "landing.faq.a2" },
    { qKey: "landing.faq.q3", aKey: "landing.faq.a3" },
    { qKey: "landing.faq.q4", aKey: "landing.faq.a4" },
    { qKey: "landing.faq.q5", aKey: isForecastingEnabled() ? "landing.faq.a5" : "landing.faq.a5NoForecast" },
    { qKey: "landing.faq.q6", aKey: "landing.faq.a6" },
    { qKey: "landing.faq.q7", aKey: "landing.faq.a7" },
];

export const FAQ = () => {
    const { t } = useTranslation("common");
    const [openIdx, setOpenIdx] = useState<number | null>(0);
    const faqs = getFaqs();

    return (
        <section id="faq" className="py-20 sm:py-28">
            <div className="container mx-auto px-4 sm:px-6">
                <div className="text-center max-w-2xl mx-auto mb-12 sm:mb-16">
                    <p className="text-xs font-semibold tracking-[0.2em] uppercase text-accent-primary mb-3">
                        {t("landing.faq.eyebrow")}
                    </p>
                    <h2 className="text-3xl sm:text-4xl font-bold font-display text-text-primary mb-4">
                        {t("landing.faq.title")}
                    </h2>
                    <p className="text-base sm:text-lg text-neutral-600 dark:text-neutral-400">
                        {t("landing.faq.subtitle")}
                    </p>
                </div>

                <div className="max-w-3xl mx-auto divide-y divide-neutral-200 dark:divide-neutral-800 border-y border-neutral-200 dark:border-neutral-800">
                    {faqs.map((f, i) => {
                        const isOpen = openIdx === i;
                        return (
                            <div key={f.qKey}>
                                <button
                                    onClick={() => setOpenIdx(isOpen ? null : i)}
                                    className="w-full flex items-center justify-between gap-4 py-5 sm:py-6 text-left group"
                                    aria-expanded={isOpen}
                                >
                                    <span className="text-base sm:text-lg font-medium text-text-primary group-hover:text-accent-primary transition-colors">
                                        {t(f.qKey)}
                                    </span>
                                    <ChevronDown
                                        size={20}
                                        className={`shrink-0 text-neutral-400 transition-transform ${isOpen ? "rotate-180 text-accent-primary" : ""}`}
                                    />
                                </button>
                                <AnimatePresence initial={false}>
                                    {isOpen && (
                                        <motion.div
                                            key="content"
                                            initial={{ height: 0, opacity: 0 }}
                                            animate={{ height: "auto", opacity: 1 }}
                                            exit={{ height: 0, opacity: 0 }}
                                            transition={{ duration: 0.25, ease: "easeOut" }}
                                            className="overflow-hidden"
                                        >
                                            <p className="pb-5 sm:pb-6 pr-8 text-sm sm:text-base text-neutral-600 dark:text-neutral-400 leading-relaxed">
                                                {t(f.aKey)}
                                            </p>
                                        </motion.div>
                                    )}
                                </AnimatePresence>
                            </div>
                        );
                    })}
                </div>
            </div>
        </section>
    );
};
